import PropTypes from "prop-types";
import React, { useEffect, useRef, useState } from "react";

import {
  getCumulativeOffset,
  getCumulativeTransformations,
  getFirstParentWithMatchingStyle,
  useDeviceInfo,
  useResizeObserver,
  window,
} from "@swa-ui/browser";
import { classNames, getUniqueId } from "@swa-ui/string";

import { PortalRoot } from "../PortalRoot";
import { Transform } from "../Transform";
import styles from "./Adjoin.module.scss";

/**
 * Adjoin provides a way to physically locate two elements so they are adjacent, typically to one
 * side or above/below.
 */
export const Adjoin = (props) => {
  const {
    adjoinedClassName,
    adjoinedContentClassName,
    adjoiningContent,
    adjoinRef,
    adjustToFit = true,
    adjustToFitCallback,
    alignment,
    children,
    className,
    id,
    inline = true,
    location = "hidden",
    mainClassName,
    portal = false,
    xOffset = 0,
    yOffset = 0,
  } = props;
  const [adjoiningContentDimensions, setAdjoiningContentDimensions] = useState({
    height: 0,
    width: 0,
  });
  const [adjoiningContentPosition, setAdjoiningContentPosition] = useState([0, 0]);
  const [currentLocation, setCurrentLocation] = useState(location);
  const [isPositioned, setIsPositioned] = useState(false);
  const [isMounted, setIsMounted] = useState(false);
  const [mainContentDimensions, setMainContentDimensions] = useState({ height: 0, width: 0 });
  const { gridPadding } = useDeviceInfo();
  const adjoiningContentRef = useRef();
  const mainContentRef = useRef();
  const scrollPosition = useRef();
  const uniqueId = useRef(getUniqueId("adjoin-id"));

  useResizeObserver({
    callback: () => {
      if (adjoiningContentRef.current) {
        const rect = adjoiningContentRef.current.getBoundingClientRect();

        setAdjoiningContentDimensions({ height: rect.height, width: rect.width });
      }
    },
    element: adjoiningContentRef,
  });

  useResizeObserver({
    callback: () => {
      if (mainContentRef.current) {
        const rect = mainContentRef.current.getBoundingClientRect();

        setMainContentDimensions({ height: rect.height, width: rect.width });
      }
    },
    element: mainContentRef,
  });

  useEffect(() => {
    setCurrentLocation(location);

    if (location === "hidden") {
      setAdjoiningContentPosition([0, 0]);
      setIsPositioned(false);
    }
  }, [location]);

  useEffect(() => {
    if (currentLocation === "full-screen") {
      scrollPosition.current = window.scrollY;
      window?.scrollTo?.(0, 0);
    } else if (scrollPosition.current) {
      window?.scrollTo?.(0, scrollPosition.current);
    }
  }, [currentLocation]);

  useEffect(() => {
    if (isMounted && adjoiningContentRef.current && mainContentRef.current) {
      setAdjoiningContentPosition(getAdjoiningContentLocation());
    }
  }, [
    adjoiningContentDimensions,
    alignment,
    currentLocation,
    isMounted,
    mainContentDimensions,
    xOffset,
    yOffset,
  ]);

  return (
    <div className={classNames(className, styles.adjoin)} ref={adjoinRef}>
      <div {...getMainContentProps()}>{children}</div>
      <div className={classNames(adjoinedClassName)} id={id}>
        {renderAdjoiningContent()}
      </div>
    </div>
  );

  function renderAdjoiningContent() {
    const content = <Transform {...getTransformProps()}>{adjoiningContent}</Transform>;

    return portal ? <PortalRoot>{content}</PortalRoot> : content;
  }

  function getMainContentProps() {
    return {
      className: classNames(mainClassName, {
        [styles.mainContainerInline]: inline,
        [styles.mainContainerBlock]: !inline,
      }),
      ref: mainContentRef,
    };
  }

  function getTransformProps() {
    return {
      "aria-hidden": currentLocation === "hidden",
      className: classNames(adjoinedContentClassName, styles.adjoiningContent, {
        [styles.hidden]: !isPositioned,
        [styles.adjacentContent]: isAbsolutelyPositioned(),
        [styles.fullScreen]: currentLocation === "full-screen",
        [styles.fullScreenFixed]: currentLocation === "full-screen-fixed",
      }),
      id: uniqueId.current,
      immediate: true,
      onTransformationEnd: handleTransformationEnd,
      ref: (element) => {
        adjoiningContentRef.current = element;
        setIsMounted(true);
      },
      transformations: getPositionTransformation(),
    };
  }

  function handleTransformationEnd(transformations) {
    const { translateX, translateY } = transformations ?? {};

    if ((translateX !== undefined || translateY !== undefined) && location !== "hidden") {
      setIsPositioned(true);
    }
  }

  function getAdjoiningContentLocation() {
    return getLocation(currentLocation, portal ? getPortalDeltas() : getRelativeDeltas());
  }

  function getPortalDeltas() {
    const mainRect = mainContentRef.current.getBoundingClientRect();

    return {
      deltaX: mainRect.left + window.scrollX,
      deltaY: mainRect.top + window.scrollY,
      fitAdjustment: getPortalFitAdjustment,
    };
  }

  function getRelativeDeltas() {
    return {
      deltaX: 0,
      deltaY: 0,
      fitAdjustment: getFitAdjustment,
    };
  }

  function getLocation(position, { deltaX, deltaY, fitAdjustment }) {
    const horizontalAlignment = getHorizontalAlignment();
    const verticalAlignment = getVerticalAlignment();
    const locations = {
      above: [
        deltaX + horizontalAlignment,
        deltaY +
          (portal
            ? -adjoiningContentDimensions.height
            : -(mainContentDimensions.height + adjoiningContentDimensions.height)),
      ],
      below: [deltaX + horizontalAlignment, deltaY + (portal ? mainContentDimensions.height : 0)],
      center: [deltaX + getHorizontalAlignment(), deltaY + getVerticalAlignment()],
      "center-to-window": [getHorizontalCenterToWindow(), getVerticalCenterToWindow()],
      "center-vertically": [deltaX, getVerticalCenterToWindow()],
      "full-screen": [getFullScreenX(), getFullScreenY()],
      "full-screen-fixed": [0, 0],
      hidden: [0, 0],
      left: [deltaX - adjoiningContentDimensions.width, deltaY + verticalAlignment],
      right: [deltaX + mainContentDimensions.width, deltaY + verticalAlignment],
      "window-top": [deltaX, getVerticalTopOfWindow()],
    };
    const locationPosition = locations[position] ?? locations["center-to-window"];

    locationPosition[0] = fitAdjustment(locationPosition[0]) + xOffset;
    locationPosition[1] += yOffset;

    return locationPosition;
  }

  function getPositionTransformation() {
    return [getTranslateX(adjoiningContentPosition[0]), getTranslateY(adjoiningContentPosition[1])];
  }

  function getFitAdjustment(x) {
    let adjustedX = x;

    if (adjustToFit) {
      if (currentLocation === "above" || currentLocation === "below") {
        const mainContainerX = mainContentRef.current.getBoundingClientRect().x;

        if (alignment !== "right" && isRightEdgeOutsideViewport(x)) {
          const rightEdgeOfWindow = getMaximumRightEdge(adjoiningContentRef.current);
          const newX = -(mainContainerX - (rightEdgeOfWindow - adjoiningContentDimensions.width));

          adjustedX = newX;
          adjustToFitCallback?.({ x: x - newX, y: 0 });
        } else if (alignment !== "left" && isLeftEdgeOutsideViewport(x)) {
          adjustedX = mainContainerX - getMinimumLeftEdge(adjoiningContentRef.current) + x;
          adjustToFitCallback?.({ x: adjustedX, y: 0 });
          adjustedX = x - adjustedX;
        }
      }
    }

    return adjustedX;
  }

  function getPortalFitAdjustment(x) {
    let adjustedX = x;

    if (adjustToFit) {
      if (currentLocation === "above" || currentLocation === "below") {
        if (alignment !== "right" && isPortalRightEdgeOutsideViewport(x)) {
          const rightEdgeOfWindow = getMaximumRightEdge(adjoiningContentRef.current);

          adjustedX = rightEdgeOfWindow - adjoiningContentDimensions.width;
          adjustToFitCallback?.({ x: adjustedX, y: 0 });
        } else if (alignment !== "left" && isPortalLeftEdgeOutsideViewport(x)) {
          adjustedX = getMinimumLeftEdge(adjoiningContentRef.current);
          adjustToFitCallback?.({ x: -adjustedX, y: 0 });
        }
      }
    }

    return adjustedX;
  }

  function isRightEdgeOutsideViewport(newX) {
    const rightEdge = getMaximumRightEdge(adjoiningContentRef.current);
    const { transformX } = getCumulativeTransformations(adjoiningContentRef.current);

    return (
      newX + getAdjoiningContentContainerX() + transformX + adjoiningContentDimensions.width >
      rightEdge
    );
  }

  function isAbsolutelyPositioned() {
    return (
      portal ||
      (currentLocation !== "center" &&
        currentLocation !== "center-to-window" &&
        currentLocation !== "center-vertically" &&
        currentLocation !== "full-screen" &&
        currentLocation !== "full-screen-fixed" &&
        currentLocation !== "window-top")
    );
  }

  function isLeftEdgeOutsideViewport(newX) {
    const { transformX } = getCumulativeTransformations(adjoiningContentRef.current);

    return (
      newX + getAdjoiningContentContainerX() + transformX <
      getMinimumLeftEdge(adjoiningContentRef.current)
    );
  }

  function isPortalRightEdgeOutsideViewport(x) {
    const rightEdge = getMaximumRightEdge(adjoiningContentRef.current);

    return x + adjoiningContentDimensions.width > rightEdge;
  }

  function isPortalLeftEdgeOutsideViewport(x) {
    return x < getMinimumLeftEdge(adjoiningContentRef.current);
  }

  function getMinimumLeftEdge(element) {
    const overflowParent = getFirstParentWithMatchingStyle(element, "overflow-x", "clip");

    return overflowParent
      ? Math.max(gridPadding, overflowParent?.getBoundingClientRect().x)
      : gridPadding;
  }

  function getMaximumRightEdge(element) {
    const overflowParent = getFirstParentWithMatchingStyle(element, "overflow-x", "clip");

    const parentRightEdge =
      overflowParent?.getBoundingClientRect().x + overflowParent?.getBoundingClientRect().width;
    const viewportRightEdge = getViewportWidth() - gridPadding;

    return parentRightEdge ? Math.min(viewportRightEdge, parentRightEdge) : viewportRightEdge;
  }

  function getViewportWidth() {
    return window.document.documentElement.clientWidth;
  }

  function getHorizontalAlignment() {
    const mainWidth = mainContentDimensions.width;
    const adjoiningWidth = adjoiningContentDimensions.width;
    let transformation;

    if (alignment === "left") {
      transformation = 0;
    } else if (alignment === "right") {
      transformation = mainWidth - adjoiningWidth;
    } else {
      transformation = mainWidth / 2 - adjoiningWidth / 2;
    }

    return transformation;
  }

  function getVerticalAlignment() {
    const mainHeight = mainContentDimensions.height;
    const adjoiningHeight = adjoiningContentDimensions.height;
    let transformation;

    if (alignment === "top") {
      transformation = portal ? 0 : -mainHeight;
    } else if (alignment === "bottom") {
      transformation = (portal ? mainHeight : 0) - adjoiningHeight;
    } else {
      const portalModifier = portal ? 1 : -1;

      transformation = (portalModifier * mainHeight) / 2 - adjoiningHeight / 2;
    }

    return transformation;
  }

  function getHorizontalCenterToWindow() {
    const centerOfWindow = window.innerWidth / 2;
    const relativeOffset = portal ? 0 : mainContentRef.current.getBoundingClientRect().x;

    return centerOfWindow - relativeOffset - adjoiningContentDimensions.width / 2;
  }

  function getVerticalCenterToWindow() {
    const adjoinContentY = getCumulativeOffset(adjoiningContentRef.current).top;
    const topOfWindow = getTopOfWindow();
    const targetY = topOfWindow + window.innerHeight / 3;
    const gapAboveAdjoiningContent = window.innerHeight / 3;
    const gapBelowAdjoiningContent =
      window.innerHeight - adjoiningContentDimensions.height - gapAboveAdjoiningContent;
    let newY = adjoinContentY - targetY;

    if (gapBelowAdjoiningContent < gapAboveAdjoiningContent) {
      newY =
        adjoinContentY -
        (topOfWindow + (window.innerHeight - adjoiningContentDimensions.height) / 2);
    }

    if (adjoinContentY - newY < topOfWindow) {
      newY = adjoinContentY - topOfWindow - gridPadding;
    }

    return -newY;
  }

  function getTopOfWindow() {
    return window.scrollY;
  }

  function getVerticalTopOfWindow() {
    return -(
      getCumulativeOffset(mainContentRef.current).top +
      mainContentDimensions.height -
      window.scrollY
    );
  }

  function getFullScreenX() {
    return -(getCumulativeOffset(adjoiningContentRef.current).left - gridPadding);
  }

  function getFullScreenY() {
    const offsetY = getCumulativeOffset(adjoiningContentRef.current).top;
    const { transformY } = getCumulativeTransformations(adjoiningContentRef.current);

    return -(offsetY + transformY - gridPadding) + window.scrollY;
  }

  function getAdjoiningContentContainerX() {
    const offsetX = getCumulativeOffset(adjoiningContentRef.current).left;

    return Math.max(offsetX, gridPadding);
  }

  function getTranslateX(amount) {
    const adjustedAmount = Number.isNaN(amount) ? 0 : amount;

    return {
      action: "translateX",
      amount: `${Math.round(adjustedAmount)}px`,
    };
  }

  function getTranslateY(amount) {
    const adjustedAmount = Number.isNaN(amount) ? 0 : amount;

    return {
      action: "translateY",
      amount: `${Math.round(adjustedAmount)}px`,
    };
  }
};

Adjoin.displayName = "Adjoin";

export const adjoinPropTypes = {
  /** className to be given to adjoining content container. */
  adjoinedClassName: PropTypes.string,

  /** className to be given to adjoining content. */
  adjoinedContentClassName: PropTypes.string,

  /** Content rendered adjacent to "main" content. */
  adjoiningContent: PropTypes.node.isRequired,

  /** Ref passed to allow useDismiss to close Adjoin on clicking outside of component. */
  adjoinRef: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({ current: PropTypes.object })]),

  /**
   * Whether to adjust the adjoining content to best fit within the bounds of the page should the
   * determined location not completely fit in the viewport. This is primarily used by the Caption
   * pointer, since the adjoining positioning is largely handled by the Caption flyout contents.
   * Can be useful to disable when the adjoining content is positioned inside of a scrolling element.
   */
  adjustToFit: PropTypes.bool,

  /**
   * When the adjoined content is located above or below the main element, and the content cannot
   * fit in window without getting outside the page margins, Adjoin will inform the caller how much
   * the content has to move to fit. When this callback is made, it will receive one object
   * parameter in this format: { x: number, y: number }. x will be negative if the content had to be
   * moved left to fit in the viewport. Currently, y will always be zero.
   */
  adjustToFitCallback: PropTypes.func,

  /** Where adjoiningContent will be placed. */
  alignment: PropTypes.oneOf(["bottom", "left", "center", "right", "top"]),

  /** Content for "main" element. */
  children: PropTypes.node,

  /**
   * Additional classes for positioning the component. Given classes may only position this component
   * for layout purposes, and cannot change how the component renders in any way.
   */
  className: PropTypes.string,

  /** Unique identifier which is passed to container element of flyout. */
  id: PropTypes.string,

  /** Whether to use CSS inline display styling on the children content. Defaults to true. */
  inline: PropTypes.bool,

  /**
   * Where adjoiningContent will be placed. If last given, adjoinedContent will be render in the same
   *  place it was rendered lastly, or centered if it was not previously rendered.
   */
  location: PropTypes.oneOf([
    "above",
    "below",
    "center",
    "center-to-window",
    "center-vertically",
    "full-screen",
    "full-screen-fixed",
    "hidden",
    "left",
    "right",
    "window-top",
  ]),

  /** className to be given to main content container. */
  mainClassName: PropTypes.string,

  /** Whether to render the adjoining content in a React portal. */
  portal: PropTypes.bool,

  /** Positive or negative value to nudge the adjoining element in the horizontal direction. */
  xOffset: PropTypes.number,

  /** Positive or negative value to nudge the adjoining element vertically. */
  yOffset: PropTypes.number,
};

Adjoin.propTypes = adjoinPropTypes;
